import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import type FloorModel from 'garaje/models/floor';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type { AtlasConfig } from 'garaje/utils/atlas';
import urlBuilder from 'garaje/utils/url-builder';
import { defer } from 'rsvp';

interface AtlasEditorHeaderComponentProps {
  config: AtlasConfig;
}

export default class AtlasEditorHeaderComponent extends Component<AtlasEditorHeaderComponentProps> {
  @service declare ajax: AjaxFetchService;
  @service declare store: Store;

  @tracked declare position: { x: number; y: number } | undefined;

  @tracked bearingOptions: { name: string; id: number }[] = [
    {
      name: 'Default',
      id: 0,
    },
    {
      name: '90 degrees',
      id: 90,
    },
    {
      name: '180 degrees',
      id: 180,
    },
    {
      name: '270 degrees',
      id: 270,
    },
  ];

  constructor(owner: unknown, args: AtlasEditorHeaderComponentProps) {
    super(owner, args);

    this.position = this.args.config.localPosition;
  }

  get currentFloor(): FloorModel | undefined {
    const floors = this.store.peekAll('floor').slice();
    const floorId = this.args.config.floorId || floors[0]?.id;

    if (!floorId) {
      return;
    }

    return floors.find((f) => f.id === floorId);
  }

  get floorImage(): string | undefined {
    return this.currentFloor?.floorPlanUrl;
  }

  get positionX(): number {
    return (this.position?.x || 0) * 100;
  }

  get positionY(): number {
    return (this.position?.y || 0) * 100;
  }

  @action
  handleFloorClick(e: PointerEvent): void {
    const { offsetX, offsetY } = e;

    // Gets the relative x & y percentages from the click
    // ex: if you click exactly in the middle, x = 0.5 and y = 0.5
    const target = e.target as HTMLDivElement;
    const x = offsetX / target.clientWidth;
    const y = offsetY / target.clientHeight;

    this.position = { x, y };
  }

  @dropTask
  updatePositionTask = {
    *perform(this: {
      context: AtlasEditorHeaderComponent;
      abort?: () => void;
      continue?: (position: { x: number; y: number } | undefined) => Promise<void>;
    }): Generator<Promise<boolean>, void, void> {
      const deferred = defer<boolean>();

      this.abort = () => {
        this.context.position = this.context.args.config.localPosition;
        deferred.resolve(false);
      };

      this.continue = async (position) => {
        const url = urlBuilder.mapi.studio.positionUrl();

        this.context.args.config.localPosition = position;
        this.context.position = this.context.args.config.localPosition;

        if (!position) {
          this.context.args.config.globalPosition = undefined;
          deferred.resolve(true);
          return;
        }

        // Ask the Map Kiosk BFF what the "grounded" coordinates are for the selected position
        const { globalPosition } = await this.context.ajax.request<{ globalPosition: [number, number] }>(url, {
          type: 'POST',
          contentType: 'application/json',
          data: JSON.stringify({
            floorId: this.context.currentFloor?.id,
            localPosition: this.context.args.config.localPosition,
          }),
        });

        this.context.args.config.globalPosition = globalPosition;

        deferred.resolve(true);
      };

      return yield deferred.promise;
    },
  };
}
